// client/src/components/configuracoes/plataformaBranding/PlataformaBrandingConfigPage.js
// Logo da própria plataforma (SituAI) — a mesma imagem exibida na sidebar
// web e no drawer do app mobile pra qualquer usuário logado. Só matriz
// pode trocar; espelha o padrão de PlataformaNotaFiscalConfigPage.js.
//
// Duas variantes (light/dark) — a Identidade Visual SituAI define regra
// própria de aplicação por fundo ("Fundo escuro: nome em branco #FFFFFF" /
// "Fundo claro: nome em grafite #0F172A"). Uma logo só nunca fica legível
// nos dois contextos (confirmado ao vivo: a logo única enviada antes desta
// mudança ficava ilegível no rodapé escuro do site institucional).
import React, { useRef, useState } from 'react';
import { Image as ImageIcon, Trash2, Upload } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { usePlataformaBranding, useUploadPlataformaBranding, useRemovePlataformaBranding } from '../../../hooks/usePlataformaBranding';
import useAlert from '../../../hooks/useAlert';

const VARIANTES = [
    {
        key: 'light',
        field: 'logoLightUrl',
        titulo: 'Logo para fundo claro',
        descricaoVazia: 'Sem logo enviada — usada em superfícies claras (fundo branco/cinza claro). Nome aparece em grafite na arte.',
        descricaoPresente: 'Usada em superfícies claras — sidebar web, cards e fundos brancos.',
        previewBg: '#F8FAFC',
    },
    {
        key: 'dark',
        field: 'logoDarkUrl',
        titulo: 'Logo para fundo escuro',
        descricaoVazia: 'Sem logo enviada — usada em superfícies escuras (navbar, rodapé, sidebar escura). Nome aparece em branco na arte.',
        descricaoPresente: 'Usada em superfícies escuras — navbar, rodapé e sidebars com fundo escuro.',
        previewBg: '#0F172A',
    },
];

const LogoVariantCard = ({ variante, logoUrl }) => {
    const uploadBranding = useUploadPlataformaBranding();
    const removeBranding = useRemovePlataformaBranding();
    const { setAlert } = useAlert();
    const fileInputRef = useRef(null);
    const [preview, setPreview] = useState(null);

    const logoAtual = preview || logoUrl || null;
    const isPending = (uploadBranding.isPending && uploadBranding.variables?.variant === variante.key)
        || (removeBranding.isPending && removeBranding.variables === variante.key);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setAlert('O arquivo não pode exceder 5MB.', 'danger');
            return;
        }

        setPreview(URL.createObjectURL(file));
        uploadBranding.mutate({ file, variant: variante.key }, {
            onSettled: () => { e.target.value = ''; },
        });
    };

    const handleRemove = () => {
        if (!window.confirm(`Remover a "${variante.titulo}"? Volta a mostrar o texto/ícone padrão nesse contexto.`)) return;
        setPreview(null);
        removeBranding.mutate(variante.key);
    };

    return (
        <div className="form-section">
            <h4 className="form-section-title">{variante.titulo}</h4>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                <div
                    style={{
                        width: 96, height: 96, borderRadius: 16, border: '1px dashed var(--color-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                        background: variante.previewBg, flexShrink: 0,
                    }}
                >
                    {logoAtual ? (
                        <img src={logoAtual} alt={variante.titulo} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                        <ImageIcon size={28} className="icon" style={{ color: variante.key === 'dark' ? '#94A3B8' : 'var(--color-text-secondary)' }} />
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <p className="form-hint" style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                        {logoUrl ? variante.descricaoPresente : variante.descricaoVazia}
                        <br />PNG, JPG, SVG ou WEBP, até 5MB. Fundo transparente recomendado.
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                            type="button"
                            className="action-button action-button-primary"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isPending}
                        >
                            <Upload size={18} className="icon" /> {isPending && uploadBranding.isPending ? 'Enviando...' : (logoUrl ? 'Trocar Logo' : 'Enviar Logo')}
                        </button>
                        {logoUrl && (
                            <button
                                type="button"
                                className="action-button action-button-danger"
                                onClick={handleRemove}
                                disabled={isPending}
                            >
                                <Trash2 size={18} className="icon" /> Remover
                            </button>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </div>
            </div>
        </div>
    );
};

const PlataformaBrandingConfigPage = () => {
    const { data: branding, isLoading } = usePlataformaBranding();

    if (isLoading) return null;

    return (
        <div className="page-management-container">
            <PageHeader icon={<ImageIcon />} title="Logo da Plataforma" subtitle="Identidade visual exibida na sidebar e no app, pra todos os tenants" />

            <div className="content-panel">
                {VARIANTES.map((variante) => (
                    <LogoVariantCard key={variante.key} variante={variante} logoUrl={branding?.[variante.field]} />
                ))}
            </div>
        </div>
    );
};

export default PlataformaBrandingConfigPage;
